import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { type ColumnConfig, ColumnSelector } from './ColumnSelector';
import {
  DEFAULT_ACCOUNT_COLUMNS,
  DEFAULT_CONTACT_COLUMNS,
  parseColumnsConfig
} from './defaultColumns';
import { FilterBuilder } from './FilterBuilder';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useFieldSelections } from '@/hooks/useFieldSelections';

const UPDATE_SAVED_VIEW = gql`
  mutation UpdateSavedView($id: ID!, $values: SavedViewInput!) {
    updateSavedView(id: $id, values: $values) {
      ok
      error
      savedView {
        id
        name
      }
    }
  }
`;

const DELETE_SAVED_VIEW = gql`
  mutation DeleteSavedView($id: ID!) {
    deleteSavedView(id: $id) {
      ok
      error
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
      completeName
    }
  }
`;

interface EditViewDialogProps {
  onOpenChange: (open: boolean) => void;
  onViewUpdated: () => void;
  open: boolean;
  view: View;
}

interface View {
  accessCount?: number;
  category?: string;
  columnsConfig?: string;
  createdAt?: string;
  createdByName?: string;
  departmentId?: string;
  departmentName?: string;
  description?: string;
  filterJson?: string;
  id: string;
  lastAccessed?: string;
  name: string;
  ownerName?: string;
  recordCount?: number;
  targetModel: string;
  visibility: string;
}

export function EditViewDialog({
  onOpenChange,
  onViewUpdated,
  open,
  view
}: EditViewDialogProps) {
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [category, setCategory] = useState('');
  const [targetModel, setTargetModel] = useState('account');
  const [visibility, setVisibility] = useState('private');
  const [description, setDescription] = useState('');
  const [filterJson, setFilterJson] = useState('[]');
  const [selectedColumns, setSelectedColumns] = useState<ColumnConfig[]>([]);
  const [activeTab, setActiveTab] = useState('general');

  const [updateView, { loading: updating }] = useMutation(UPDATE_SAVED_VIEW);
  const [deleteView, { loading: deleting }] = useMutation(DELETE_SAVED_VIEW);
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);

  // Fetch field selections from backend for dropdowns
  const { options: targetModelOptions } = useFieldSelections(
    'opstream.saved.view',
    'target_model'
  );
  const { options: visibilityOptions } = useFieldSelections(
    'opstream.saved.view',
    'visibility'
  );

  const departments = departmentsData?.departments || [];

  // Populate form when view changes
  useEffect(() => {
    if (view) {
      setName(view.name);
      setDepartmentId(view.departmentId || '');
      setCategory(view.category || '');
      setTargetModel(view.targetModel);
      setVisibility(view.visibility);
      setDescription(view.description || '');
      setFilterJson(view.filterJson || '[]');

      // Parse columns config using the shared utility
      const parsedColumns = parseColumnsConfig(
        view.columnsConfig,
        view.targetModel as 'account' | 'contact'
      );
      setSelectedColumns(parsedColumns);
    }
  }, [view]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate JSON
    try {
      JSON.parse(filterJson);
    } catch (err) {
      toast.error(
        'Invalid Filter JSON: Please provide valid JSON for the filter criteria'
      );
      setActiveTab('filters');
      return;
    }

    // Serialize columns config
    const columnsConfig =
      selectedColumns.length > 0 ? JSON.stringify({ selectedColumns }) : null;

    const updateValues = {
      category: category || null,
      columnsConfig,
      departmentId: departmentId || null,
      description: description || null,
      filterJson,
      name,
      targetModel,
      visibility
    };

    try {
      const { data } = await updateView({
        variables: {
          id: view.id,
          values: updateValues
        }
      });

      if (data?.updateSavedView?.ok) {
        toast.success(`Successfully updated view "${name}"`);
        onViewUpdated();
      } else {
        toast.error(data?.updateSavedView?.error || 'Failed to update view');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete the view "${view.name}"?`)) {
      return;
    }

    try {
      const { data } = await deleteView({
        variables: { id: view.id }
      });

      if (data?.deleteSavedView?.ok) {
        toast.success(`Successfully deleted view "${view.name}"`);
        onViewUpdated();
        onOpenChange(false);
      } else {
        toast.error(data?.deleteSavedView?.error || 'Failed to delete view');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit Saved View</DialogTitle>
          <DialogDescription>
            Update your saved view settings and filters
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={handleSubmit}>
            <Tabs
              className="space-y-4"
              onValueChange={setActiveTab}
              value={activeTab}
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="filters">Filters</TabsTrigger>
                <TabsTrigger value="columns">Columns</TabsTrigger>
              </TabsList>

              <TabsContent
                className="mt-0 space-y-4"
                value="general"
              >
                <Field className="gap-2">
                  <FieldLabel htmlFor="name">View Name *</FieldLabel>
                  <Input
                    id="name"
                    onChange={e => {
                      setName(e.target.value);
                    }}
                    placeholder="e.g., High Priority Investors"
                    required
                    value={name}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field className="gap-2">
                    <FieldLabel htmlFor="department">Department</FieldLabel>
                    <Select
                      onValueChange={value => {
                        setDepartmentId(value === '__none__' ? '' : value);
                      }}
                      value={departmentId || '__none__'}
                    >
                      <SelectTrigger id="department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No Department</SelectItem>
                        {departments.map((dept: any) => (
                          <SelectItem
                            key={dept.id}
                            value={dept.id}
                          >
                            {dept.completeName || dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field className="gap-2">
                    <FieldLabel htmlFor="category">Category</FieldLabel>
                    <Input
                      id="category"
                      onChange={e => {
                        setCategory(e.target.value);
                      }}
                      placeholder="e.g., Fundraising, Investor Relations"
                      value={category}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field className="gap-2">
                    <FieldLabel htmlFor="targetModel">
                      Target Model *
                    </FieldLabel>
                    <Select
                      onValueChange={setTargetModel}
                      value={targetModel}
                    >
                      <SelectTrigger id="targetModel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {targetModelOptions.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field className="gap-2">
                    <FieldLabel htmlFor="visibility">Visibility *</FieldLabel>
                    <Select
                      onValueChange={setVisibility}
                      value={visibility}
                    >
                      <SelectTrigger id="visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {visibilityOptions.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field className="gap-2">
                  <FieldLabel htmlFor="description">Description</FieldLabel>
                  <Textarea
                    id="description"
                    onChange={e => {
                      setDescription(e.target.value);
                    }}
                    placeholder="Describe what this view contains..."
                    rows={3}
                    value={description}
                  />
                </Field>
              </TabsContent>

              <TabsContent
                className="mt-0"
                value="filters"
              >
                <FilterBuilder
                  onChange={setFilterJson}
                  targetModel={targetModel as 'account' | 'contact'}
                  value={filterJson}
                />
              </TabsContent>

              <TabsContent
                className="mt-0"
                value="columns"
              >
                <ColumnSelector
                  onChange={setSelectedColumns}
                  selectedColumns={selectedColumns}
                  targetModel={targetModel as 'account' | 'contact'}
                />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button
                disabled={deleting}
                onClick={handleDelete}
                type="button"
                variant="destructive"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
              <div className="flex-1" />
              <Button
                onClick={() => {
                  onOpenChange(false);
                }}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={updating}
                type="submit"
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
